import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Package as PackageIcon, PlusCircle, Loader, Truck, Filter, CreditCard, FileText, CheckCircle, X, DollarSign, AlertCircle } from 'lucide-react';
import { usePackages } from '../hooks/usePackages';
import { usePackageSummary } from '../hooks/usePackageSummary';
import { useDeliveryAvailability } from '../hooks/useDeliveryAvailability';
import { CourierPackage } from '@shared/sdk-v1/models';
import { formatWeightDisplay } from '../utils/weightUtils';
import { formatCurrency } from '../utils/currencyUtils';
import { useAuth } from '../auth/hooks/useAuth';
import { useSelector } from 'react-redux';
import { getCourier, getCurrency } from '@state/courierSlice';
import { useTrackEvent } from '../utils/track';
import { resolvePaymentGateway, buildPaymentInitiatedParams } from '../utils/payment';

const Packages: React.FC = () => {
  const navigate = useNavigate();
  const courier = useSelector(getCourier);
  const track = useTrackEvent();
  const { isDeliveryAvailable } = useDeliveryAvailability();
  const { readyForPickup, totalOutstanding, isLoading: isSummaryLoading } = usePackageSummary();
  const currency = useSelector(getCurrency);
  const [searchTerm, setSearchTerm] = useState('');
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [showInvoiceFilter, setShowInvoiceFilter] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState('All');
  const [changingStatus, setChangingStatus] = useState(false);
  const [selectedForPayment, setSelectedForPayment] = useState<number[]>([]);
  const [showPaymentSummary, setShowPaymentSummary] = useState(false);
  const [showDelivered, setShowDelivered] = useState(false);
  const ITEMS_PER_PAGE = 20;

  // Use the packages hook
  const {
    packages,
    isLoading,
    error,
    totalCount,
    fetchPackages,
    searchPackages,
    refresh,
    hasPaymentGateway
  } = usePackages({
    limit: ITEMS_PER_PAGE,
    skip: 0,
    order: ['createdOn DESC']
  });

  // Status options
  const statusOptions = ['All', 'Pre Alert', 'In Transit', 'Received in Jamaica', 'At Sorting Facility', 'At Warehouse', 'Ready', 'Out for Delivery', 'Delivered', 'Jamaica Customs', 'Invoice Required', 'Delayed'];
  // Invoice status options
  const invoiceStatusOptions = ['All', 'With Invoice', 'Without Invoice', 'Paid', 'Unpaid', 'Pending Verification'];
  const [currentPage, setCurrentPage] = useState(1);

  // Show payment summary when items are selected
  useEffect(() => {
    setShowPaymentSummary(selectedForPayment.length > 0);
  }, [selectedForPayment]);

  // Build filter object based on current filter state (excluding search)
  const buildFilterObject = useCallback(() => {
    const where: Record<string, any> = {
      and: []
    };

    // If showDelivered toggle is on, show only delivered packages
    if (showDelivered) {
      where.and.push({ status: 'Delivered' });
    } else {
      // Status filter
      if (selectedStatus !== 'All') {
        where.and.push({ status: selectedStatus });
      }

      // Hide delivered packages by default unless user explicitly selected "Delivered" status
      if (selectedStatus !== 'Delivered') {
        where.and.push({ status: { neq: 'Delivered' } });
      }
    }

    return where.and.length > 0 ? where : undefined;
  }, [selectedStatus, showDelivered]);

  // Handle search with debouncing
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  const handleSearch = useCallback((searchValue: string) => {
    setSearchTerm(searchValue);

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for search
    const timeout = setTimeout(() => {
      if (searchValue.trim()) {
        track('search', { search_term: searchValue, search_context: 'packages' });
        searchPackages(searchValue);
      } else {
        // If search is cleared, fetch packages with current filters
        setCurrentPage(1);
        fetchPackages({
          limit: ITEMS_PER_PAGE,
          skip: 0,
          where: buildFilterObject(),
          order: ['createdOn DESC'],
          append: false
        });
      }
    }, 300); // 300ms debounce

    setSearchTimeout(timeout);
  }, [buildFilterObject, track]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Filter packages by invoice status on the client side
  const filteredPackages = useMemo(() => {
    if (selectedInvoiceStatus === 'All') {
      return packages;
    }

    return packages.filter((pkg: any) => {
      switch (selectedInvoiceStatus) {
        case 'With Invoice':
          return pkg.invoice && pkg.invoice.id;
        case 'Without Invoice':
          return !pkg.invoice || !pkg.invoice.id;
        case 'Paid':
          return pkg.invoice && pkg.invoice.status === 'Paid';
        case 'Unpaid':
          return pkg.invoice && pkg.invoice.status === 'Unpaid';
        case 'Pending Verification':
          return pkg.invoice && pkg.invoice.status === 'PENDING_VERIFICATION';
        default:
          return true;
      }
    });
  }, [packages, selectedInvoiceStatus]);

  // Load more packages
  const loadMorePackages = useCallback(() => {
    if (!isLoading) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchPackages({
        limit: ITEMS_PER_PAGE,
        skip: (nextPage - 1) * ITEMS_PER_PAGE,
        where: buildFilterObject(),
        order: ['createdOn DESC'],
        append: true
      });
    }
  }, [currentPage, isLoading, buildFilterObject]);

  // Apply filters and fetch packages
  useEffect(() => {
    // Reset page and fetch with new filters
    setCurrentPage(1);
    fetchPackages({
      limit: ITEMS_PER_PAGE,
      skip: 0,
      where: buildFilterObject(),
      order: ['createdOn DESC'],
      append: false
    });
  }, [selectedStatus, showDelivered, buildFilterObject]);

  // Reset loading states when packages are loaded
  useEffect(() => {
    if (!isLoading) {
      setChangingStatus(false);
    }
  }, [isLoading]);

  // Check if there are any Ready packages
  const hasReadyPackagesForDelivery = filteredPackages.some(pkg => pkg.status === 'Ready');

  // Check if package has unpaid invoice
  const hasUnpaidInvoice = (pkg: CourierPackage) => {
    return pkg.invoice && pkg.invoice.status === 'Unpaid';
  };

  // Get all packages with unpaid invoices
  const packagesWithUnpaidInvoices = filteredPackages.filter(hasUnpaidInvoice);

  // Toggle package selection for payment
  const togglePackageSelection = (e: React.MouseEvent, packageId: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedForPayment.includes(packageId)) {
      setSelectedForPayment(selectedForPayment.filter(id => id !== packageId));
    } else {
      setSelectedForPayment([...selectedForPayment, packageId]);
    }
  };

  // Select all unpaid invoices
  const selectAllUnpaidInvoices = (e: React.MouseEvent) => {
    e.preventDefault();
    const unpaidPackageIds = packagesWithUnpaidInvoices.map(pkg => pkg.id || 0).filter(id => id !== 0);
    if (selectedForPayment.length === unpaidPackageIds.length) {
      // If all are selected, deselect all
      setSelectedForPayment([]);
    } else {
      // Otherwise select all unpaid invoices
      setSelectedForPayment(unpaidPackageIds);
    }
  };

  // Calculate total amount to pay
  const calculateTotalAmount = () => {
    return filteredPackages
      .filter(pkg => pkg.id && selectedForPayment.includes(pkg.id))
      .reduce((total, pkg) => {
        return total + (pkg.invoice?.amount || 0);
      }, 0);
  };

  // Handle payment for selected invoices
  const handlePaySelectedInvoices = () => {
    if (selectedForPayment.length === 0) return;

    // Get the first package to determine courier ID
    const firstPackage = filteredPackages.find(pkg => pkg.id && selectedForPayment.includes(pkg.id));
    if (!firstPackage?.courierId) return;

    // Get all invoice IDs
    const invoiceIds = filteredPackages
      .filter(pkg => pkg.id && selectedForPayment.includes(pkg.id) && pkg.invoice)
      .map(pkg => pkg.invoice?.id)
      .filter(Boolean)
      .join(',');

    // Determine payment gateway from already-loaded courier data in Redux
    const hasStripe = Boolean(courier?.stripeCredentials?.isStripeApiEnabled);
    const hasWipay = Boolean(courier?.wipayCredentials?.isWipayApiEnabled);
    const hasFygaro = Boolean(courier?.courierSetting?.isFygaroApiEnabled);

    // Open payment link in new tab
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    let paymentUrl = '';

    if (hasStripe) {
      paymentUrl = `${baseUrl}/Stripes/make-payment?courierId=${firstPackage.courierId}&ids=${invoiceIds}`;
    } else if (hasWipay) {
      paymentUrl = `${baseUrl}/Wipays/make-payment?courierId=${firstPackage.courierId}&ids=${invoiceIds}`;
    } else if (hasFygaro) {
      paymentUrl = `${baseUrl}/Invoices/make-payment?courierId=${firstPackage.courierId}&ids=${invoiceIds}`;
    }

    if (paymentUrl) {
      track(
        'payment_initiated',
        buildPaymentInitiatedParams({
          value: calculateTotalAmount(),
          courierSetting: courier?.courierSetting,
          paymentGateway: resolvePaymentGateway(courier),
          invoiceCount: selectedForPayment.length,
        })
      );
      window.open(paymentUrl, '_blank');
    }

    // Reset selection after payment is initiated
    setSelectedForPayment([]);
  };

  // Navigate to delivery request page
  const handleRequestDelivery = () => {
    navigate('/delivery-request');
  };

  // Get status badge style
  const getStatusBadgeStyle = (status: string): string => {
    switch (status) {
      case 'Ready':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300';
      case 'In Transit':
        return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-300';
      case 'Delivered':
        return 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300';
      case 'At Warehouse':
        return 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-300';
      case 'Pre Alert':
        return 'bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-300';
      case 'Out for Delivery':
        return 'bg-teal-100 dark:bg-teal-900 text-teal-600 dark:text-teal-300';
      case 'Jamaica Customs':
        return 'bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300';
      case 'Invoice Required':
        return 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
    }
  };

  // Get invoice badge style
  const getInvoiceBadgeStyle = (status: string): string => {
    if (status === 'Paid') {
      return 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300';
    } else {
      return 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300';
    }
  };

  // Check if package can have an invoice
  const canHaveInvoice = (status: string): boolean => {
    return ['Ready', 'Out for Delivery', 'Delivered'].includes(status);
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto p-6 pb-24 md:pb-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Outstanding Balance</p>
            {isSummaryLoading ? (
              <div className="h-7 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mt-1" />
            ) : (
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {formatCurrency(totalOutstanding, currency)}
              </p>
            )}
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Ready for Pickup</p>
            {isSummaryLoading ? (
              <div className="h-7 w-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mt-1" />
            ) : (
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {readyForPickup}
              </p>
            )}
          </div>
        </div>
        {/* Search and Filter Bar */}
        <div className="mb-6 space-y-2">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              placeholder="Search packages..."
              value={searchTerm}
              onChange={e => handleSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  setShowStatusFilter(!showStatusFilter);
                  if (showInvoiceFilter && !showStatusFilter) setShowInvoiceFilter(false);
                }}
                className="text-sm text-primary dark:text-primary flex items-center px-3 py-1 border border-primary/30 rounded-md"
              >
                <Filter className="h-4 w-4 mr-1" />
                Status
              </button>
              <button
                onClick={() => {
                  setShowInvoiceFilter(!showInvoiceFilter);
                  if (showStatusFilter && !showInvoiceFilter) setShowStatusFilter(false);
                }}
                className="text-sm text-primary dark:text-primary flex items-center px-3 py-1 border border-primary/30 rounded-md"
              >
                <FileText className="h-4 w-4 mr-1" />
                Invoice
              </button>
              <button
                onClick={() => setShowDelivered(!showDelivered)}
                className={`text-sm flex items-center px-3 py-1 border rounded-md ${
                  showDelivered
                    ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700'
                    : 'text-primary dark:text-primary border-primary/30'
                }`}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                {showDelivered ? 'Viewing Delivered' : 'View Delivered'}
              </button>
              {packagesWithUnpaidInvoices.length > 0 && hasPaymentGateway && (
                <button
                  onClick={selectAllUnpaidInvoices}
                  className="text-sm text-primary dark:text-primary flex items-center px-3 py-1 border border-primary/30 rounded-md"
                >
                  <CreditCard className="h-4 w-4 mr-1" />
                  {selectedForPayment.length === packagesWithUnpaidInvoices.length ? 'Deselect All Invoices' : 'Select All Unpaid Invoices'}
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">

              <div className="flex gap-2 ml-auto">
                {isDeliveryAvailable && hasReadyPackagesForDelivery && (
                  <button
                    onClick={handleRequestDelivery}
                    className="text-sm text-primary dark:text-primary flex items-center"
                  >
                    <Truck className="h-4 w-4 mr-1" />
                    Request Delivery
                  </button>
                )}
                {courier?.courierSetting?.isPreAlertSupported && (
                  <Link to="/packages/add" className="text-sm text-primary dark:text-primary flex items-center">
                    <PlusCircle className="h-4 w-4 mr-1" />
                    Pre-Alert Package
                  </Link>
                )}
              </div>
            </div>
          </div>
          {/* Status Filter */}
          {showStatusFilter && (
            <div className="flex flex-wrap gap-2 mt-2">
              {statusOptions.map(status => (
                <button
                  key={status}
                  onClick={() => {
                    setChangingStatus(true);
                    // Toggle off if clicking the same status, otherwise set new status
                    const next = selectedStatus === status ? 'All' : status;
                    if (next !== 'All') {
                      track('filter_applied', {
                        filter_type: 'status',
                        filter_value: next,
                        context: 'packages',
                      });
                    }
                    setSelectedStatus(next);
                  }}
                  disabled={changingStatus}
                  className={`text-xs px-3 py-1 rounded-full flex items-center ${
                    selectedStatus === status
                      ? 'bg-primary text-white dark:bg-primary'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  } ${changingStatus && selectedStatus === status ? 'opacity-75' : ''}
                    disabled:cursor-not-allowed`}
                >
                  {changingStatus && selectedStatus === status ? (
                    <>
                      <span className="inline-block h-3 w-3 border-2 border-current border-t-transparent rounded-full animate-spin mr-1"></span>
                      {status}
                    </>
                  ) : (
                    status
                  )}
                </button>
              ))}
            </div>
          )}
          {/* Invoice Filter */}
          {showInvoiceFilter && (
            <div className="flex flex-wrap gap-2 mt-2">
              {invoiceStatusOptions.map(status => (
                <button
                  key={status}
                  onClick={() => {
                    if (status !== 'All') {
                      track('filter_applied', {
                        filter_type: 'invoice_status',
                        filter_value: status,
                        context: 'packages',
                      });
                    }
                    setSelectedInvoiceStatus(status);
                  }}
                  className={`text-xs px-3 py-1 rounded-full ${
                    selectedInvoiceStatus === status
                      ? 'bg-primary text-white dark:bg-primary'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          )}
          {/* Selected for payment counter */}
          {selectedForPayment.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-3 mt-2 flex justify-between items-center">
              <div className="flex items-center">
                <CreditCard className="h-5 w-5 text-primary dark:text-primary mr-2" />
                <span className="text-sm text-blue-700 dark:text-blue-300">
                  {selectedForPayment.length} invoice
                  {selectedForPayment.length !== 1 ? 's' : ''} selected for
                  payment
                </span>
              </div>
              <button
                onClick={() => setSelectedForPayment([])}
                className="text-sm text-red-500 dark:text-red-400 flex items-center"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </button>
            </div>
          )}
        </div>
        {/* Packages List */}
        <div className="space-y-4">
          {isLoading && packages.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 text-center">
              <Loader className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
              <p className="text-gray-500 dark:text-gray-400">Loading packages...</p>
            </div>
          ) : (!isLoading && error) ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-lg p-6 text-center">
              <AlertCircle className="h-8 w-8 text-red-500 dark:text-red-400 mx-auto mb-2" />
              <p className="text-red-700 dark:text-red-300">
                {(typeof error === 'string' ? error : error?.message) || 'Failed to load packages'}
              </p>
              <button
                onClick={() => refresh()}
                className="mt-4 px-4 py-2 bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-300 rounded-md hover:bg-red-200 dark:hover:bg-red-700"
              >
                Try Again
              </button>
            </div>
          ) : filteredPackages.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 text-center">
              <p className="text-gray-500 dark:text-gray-400">
                {packages.length === 0 ? 'No packages found.' : 'No packages match the current filters.'}
              </p>
            </div>
          ) : (
            <>
              {/* Search Results Indicator */}
              {searchTerm && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Search className="h-4 w-4 text-primary dark:text-primary mr-2" />
                      <span className="text-sm text-blue-700 dark:text-blue-300">
                        Search results for "{searchTerm}": {filteredPackages.length} package{filteredPackages.length !== 1 ? 's' : ''} found
                      </span>
                    </div>
                    <button
                      onClick={() => handleSearch('')}
                      className="text-sm text-primary dark:text-primary hover:text-primary dark:hover:text-blue-300"
                    >
                      Clear Search
                    </button>
                  </div>
                </div>
              )}
              {filteredPackages.map(pkg => (
                <div
                  key={pkg.id}
                  className={`block bg-white dark:bg-gray-800 rounded-lg shadow-md border ${
                    pkg.id && selectedForPayment.includes(pkg.id) && hasUnpaidInvoice(pkg)
                      ? 'border-blue-300 dark:border-blue-700 ring-2 ring-blue-200 dark:ring-blue-800'
                      : 'border-gray-200 dark:border-gray-700'
                  } hover:shadow-lg transition-shadow relative`}
                >
                                    {hasUnpaidInvoice(pkg) && pkg.id && hasPaymentGateway && (
                    <div
                      className="absolute top-3 left-3 z-10"
                      onClick={e => togglePackageSelection(e, pkg.id as number)}
                    >
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center cursor-pointer ${
                          selectedForPayment.includes(pkg.id as number)
                            ? 'bg-primary border-primary dark:bg-primary dark:border-primary'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                        }`}
                      >
                        {selectedForPayment.includes(pkg.id as number) && (
                          <CheckCircle className="h-5 w-5 text-white" />
                        )}
                      </div>
                    </div>
                  )}
                  <Link to={`/packages/${pkg.id}`} className={`block p-4 ${hasUnpaidInvoice(pkg) && hasPaymentGateway ? 'pl-12' : ''}`}>
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex items-center min-w-0 flex-1">
                        <div className="bg-orange-100 dark:bg-orange-900 rounded-full p-2 mr-3 flex-shrink-0">
                          <PackageIcon className="h-5 w-5 text-orange-500 dark:text-orange-400" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-medium dark:text-white truncate">
                            {pkg.trackingNo}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                            {pkg.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end flex-shrink-0">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusBadgeStyle(pkg.status || 'Unknown')}`}>
                          {pkg.status || 'Unknown'}
                        </span>

                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                      <div className="overflow-hidden">
                        <span className="text-gray-500 dark:text-gray-400">
                          Weight:
                        </span>{' '}
                        <span className="dark:text-white truncate block">
                          {formatWeightDisplay(pkg.weight, courier?.courierSetting?.weightRoundingMode)}
                        </span>
                      </div>
                      {pkg.merchant && (
                        <div className="overflow-hidden">
                          <span className="text-gray-500 dark:text-gray-400">
                            Merchant:
                          </span>{' '}
                          <span className="dark:text-white truncate block">
                            {pkg.merchant}
                          </span>
                        </div>
                      )}
                      <div className="overflow-hidden">
                        <span className="text-gray-500 dark:text-gray-400">
                          Last Updated:
                        </span>{' '}
                        <span className="dark:text-white truncate block">
                          {pkg.lastUpdatedOn ? new Date(pkg.lastUpdatedOn).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                      {/* Invoice Information - Only show for Ready, Out for Delivery, and Delivered packages */}
                      <div>
                      <div className="overflow-hidden mb-1">
                        {canHaveInvoice(pkg.status || 'Unknown') ? pkg.invoice ? (
                          <div className="flex items-center flex-wrap">
                            <CreditCard className="h-4 w-4 text-primary dark:text-primary mr-1 flex-shrink-0" />
                            <span className="text-gray-500 dark:text-gray-400 mr-1 whitespace-nowrap">
                              Invoice:
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getInvoiceBadgeStyle(pkg.invoice.status || 'Unknown')}`}>
                              {pkg.invoice.status || 'Unknown'}
                            </span>
                            <span className="ml-1 dark:text-white truncate">
                              ${(pkg.invoice.amount || 0).toFixed(2)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-500 dark:text-gray-400">
                            
                          </span>
                        ) : (
                          <span className="text-gray-500 dark:text-gray-400">
                            
                          </span>
                        )}
                      </div>
                      {pkg.lateFee > 0 && (
                      <div className="overflow-hidden">
                        <span className="text-error dark:text-gray-400">
                          Late Fee: ${pkg.lateFee}.00
                        </span>
                      </div>
                      )}
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
              {/* Load More Button */}
              {filteredPackages.length > 0 && !searchTerm && (packages.length < totalCount || isLoading) && (
                <div className="mt-6 text-center">
                  <button
                    onClick={loadMorePackages}
                    disabled={isLoading}
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary disabled:bg-blue-300 dark:disabled:bg-blue-800 flex items-center justify-center mx-auto"
                  >
                    {isLoading ? (
                      <>
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      `Load More (${packages.length} of ${totalCount})`
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Payment Summary Panel - Fixed at bottom */}
      {showPaymentSummary && hasPaymentGateway && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg z-50 transition-all">
          <div className="max-w-4xl mx-auto p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">
                  Pay {selectedForPayment.length} Invoice
                  {selectedForPayment.length !== 1 ? 's' : ''}
                </h3>
                <div className="flex items-center mt-1">
                  <DollarSign className="h-4 w-4 text-green-500 dark:text-green-400 mr-1" />
                  <span className="text-lg font-bold text-green-600 dark:text-green-400">
                    ${calculateTotalAmount().toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="flex gap-3 w-full sm:w-auto">
                <button
                  onClick={() => setSelectedForPayment([])}
                  className="flex-1 sm:flex-initial px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePaySelectedInvoices}
                  className="flex-1 sm:flex-initial px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md flex items-center justify-center"
                >
                  <CreditCard className="h-5 w-5 mr-2" />
                  Pay Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Packages;
